import React, { ReactElement, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";

import OffCanvasTray from "../OffCanvasTray";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import arrowBack from "../../../assets/images/icons/arrow-left-straight.svg";
import {
  ProductItemById,
  ProductItemById_productById as Product,
  ProductItemById_productById_variants as ProductVariant,
} from "../../../types/__generated__/ProductItemById";
import { GET_PRODUCT_ITEM_BY_ID } from "../../../graphql/resolvers/reward";
import { logError } from "../../../helpers/logging";
import { colors } from "../../../styles/variables";
import { useTopInsetBg } from "../Layout/context";
import RewardMerchDetailsController from "../RewardMerchDetailsController";
import { StyledMerchTray, CloseTrayButton } from "./style";

export enum RewardMerchTrayContent {
  RewardMerchDetails = "RewardMerchDetails",
  AddRewardToNextBoxOrCart = "AddRewardToNextBoxOrCart",
}

interface RewardMerchDetailsTrayProps {
  rewardStoreProductId: number;
  onRewardMerchDetailsClose: Function;
  variant?: "small" | "large";
  memberId: number | undefined | null;
  memberLevel: number | undefined | null;
  pointsBalance: number | undefined | null;
  isOnMonthlyBoxToggle: boolean | undefined;
  /**
   * initialContent can be set to either RewardMerchDetails or AddRewardToNextBoxOrCart.
   * This is needed to set initialContent to AddRewardToNextBoxOrCart on the fullpage.
   * initialContent defaults to RewardMerchDetails if null.
   */
  initialContent?: RewardMerchTrayContent | null;
  /**
   * selectedProductVariant is passed down from the variants dropdown on the fullpage version.
   */
  selectedProductVariant?: Product | ProductVariant | null | undefined;
}

const RewardMerchDetailsTray = ({
  rewardStoreProductId,
  onRewardMerchDetailsClose,
  variant = "large",
  memberId,
  memberLevel,
  pointsBalance,
  isOnMonthlyBoxToggle,
  selectedProductVariant,
}: RewardMerchDetailsTrayProps) => {
  const [isSideTrayHidden, setIsSideTrayHidden] = useState<boolean>(false);
  const [productId, setProductId] = useState<number>(rewardStoreProductId);

  const {
    data: rewardStoreItemData,
    error: rewardStoreItemError,
    loading: isRewardStoreItemQueryLoading,
  } = useQuery<ProductItemById>(GET_PRODUCT_ITEM_BY_ID, {
    variables: {
      id: productId,
    },
  });

  const product = rewardStoreItemData?.productById;

  useEffect(() => {
    // If the product is a variant, we need to show the parent product instead
    // So use can see the product characteristic dropdown (size, color,...)
    if (rewardStoreItemData?.productById?.parentProduct?.id) {
      setProductId(rewardStoreItemData?.productById?.parentProduct.id);
    }
  }, [rewardStoreItemData]);

  const widthOfSideTrayInXsUpScreens = "500px";
  const isVariantSmall = variant === "small";

  const hasProductUrlPhotos = (product?.productPhotos?.length || 0) > 0;
  /* When the carousel renders, the inset zone should match its bg
   * so the colour bleeds continuously through the notch. Otherwise
   * the tray's white default keeps the inset white. */
  useTopInsetBg(hasProductUrlPhotos ? colors.merchCardDefaultBackgroundColor : null);
  if (rewardStoreItemError) {
    logError(rewardStoreItemError, { location: "RewardMerchDetailsTray", action: "GET_PRODUCT_ITEM_BY_ID" });
  }

  const handleCloseTray = () => {
    setIsSideTrayHidden(true);
    setTimeout(() => onRewardMerchDetailsClose(), 200);
  };

  const renderRewardMerchTray = (): ReactElement => (
    <OffCanvasTray
      setOverflowHidden={false}
      isOffcanvasOpened
      outSideClickHandler={() => handleCloseTray()}
      disableScrollLockInSideTray={false}
    >
      <StyledMerchTray
        isOpened={!isSideTrayHidden}
        hide={isSideTrayHidden}
        isVariantSmall={isVariantSmall}
        widthXsUp={widthOfSideTrayInXsUpScreens}
        topPadding={hasProductUrlPhotos ? "0px" : "40px"}
      >
        <CloseTrayButton onClick={() => handleCloseTray()}>
          <img src={arrowBack} alt="Close button" />
        </CloseTrayButton>
        {isRewardStoreItemQueryLoading ? (
          <StyledLoading />
        ) : (
          product && (
            <RewardMerchDetailsController
              variant={variant}
              memberId={memberId}
              memberLevel={memberLevel}
              memberPointsBalance={pointsBalance}
              imageGalleryWidth={widthOfSideTrayInXsUpScreens}
              product={product}
              isOnMonthlyBoxToggle={isOnMonthlyBoxToggle}
              onAddMerchSuccess={() => handleCloseTray()}
              selectedProductVariant={selectedProductVariant}
            />
          )
        )}
      </StyledMerchTray>
    </OffCanvasTray>
  );

  return renderRewardMerchTray();
};

export default RewardMerchDetailsTray;
